import { ReceiverProfileFormData } from '@/types/profile/ProfileFormData';
import { Control, useController } from 'react-hook-form';
import StyledFormSection from '../styled/StyledFormSection';
import StyledInput from '../styled/StyledInput';
import StyledDropZone from '../styled/StyledDropZone';
import {
  COUNTRIES,
  COUNTRIES_DATA,
  CURRENCIES,
} from '../../../../shared/CountriesData';
import StyledSelect from '../styled/StyledSelect';
import HorizontalLine from '../ui/HorizontalLine';
import { useGetMyBills } from '@/hooks/useBills';
import Loader from '../ui/Loader';
import StyledTable from '../styled/StyledTable';
import { BILL_COLUMN_DEF } from '@/types/bills/billColumnDef';
import { useMemo, useState } from 'react';
import { SortingState } from '@tanstack/react-table';
import ReceiverContacts from './ReceiverContacts';
import ReceiverReservationEmails from './ReceiverReservationEmails';
import { useChangeLabelValueWithSecondaryValue } from '@/hooks/useChangeLabelValueWithSecondaryValue';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import { LabelValue } from '@/types/global';
import { LOCALES } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { SupportedLocale } from '@/types/supported-locale.type';

interface ReceiverProfileGeneralTabFormProps {
  control: Control<ReceiverProfileFormData>;
  isEditEnabled: boolean;
  isAdmin: boolean;
  userData: {
    company: {
      imageUrl?: string;
    };
  };
}

const ReceiverProfileGeneralTabForm = ({
  control,
  isEditEnabled,
  isAdmin,
  userData,
}: ReceiverProfileGeneralTabFormProps) => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const [sorting, setSorting] = useState<SortingState>([]);
  const { data: bills, isLoading, isSuccess } = useGetMyBills();

  const changeCountryWithCurrency = useChangeLabelValueWithSecondaryValue({
    control,
    primaryName: 'company.country',
    secondaryName: 'company.currency',
    retrieveSecondary: (primaryValue) => {
      const countryData = COUNTRIES_DATA.find(
        (data) => data.Pays === primaryValue.value,
      );
      const currency = CURRENCIES.find(
        (currency) => currency.label === countryData?.Devise,
      );
      return currency;
    },
  });

  const { changeLanguage } = useLanguage();

  const LANGUAGES = useMemo(
    () =>
      LOCALES.map((lang) => ({
        label: t(`common.languages.${lang}`),
        value: lang,
      })),
    [t],
  );

  const {
    field: { onChange: onChangeMainLanguage },
  } = useController({
    control,
    name: 'mainLanguage',
  });
  return (
    <>
      {isAdmin && (
        <>
          <StyledFormSection sectionTitle={t('pages.profile.generalInfo')}>
            <StyledSelect
              options={LANGUAGES}
              control={control}
              title={t('pages.profile.mainLanguage')}
              name="mainLanguage"
              placeholder={t('pages.profile.selectLanguage')}
              onChange={(lang) => {
                const { value } = lang as LabelValue<string>;
                onChangeMainLanguage(lang);
                changeLanguage(value as SupportedLocale);
              }}
            />
            {!isEditEnabled && userData?.company?.imageUrl ? (
              <div className="w-full border border-[#E2E8F0] rounded-xl p-4 bg-gray-50">
                <img src={userData.company.imageUrl} className="h-10 w-auto" />
              </div>
            ) : (
              <>
                <StyledDropZone
                  control={control}
                  name="company.logo"
                  title={t('pages.profile.logo')}
                  placeholder={t('pages.profile.logoPlaceholder')}
                  disabled={!isEditEnabled}
                />
              </>
            )}
            <StyledInput
              control={control}
              name="company.name"
              title={t('pages.profile.companyName') + ' *'}
              placeholder={t('pages.profile.companyNamePlaceholder')}
              disabled={!isEditEnabled}
            />
            <div className="flex flex-col md:flex-row gap-4">
              <StyledInput
                control={control}
                name="lastname"
                title={t('pages.profile.lastName') + ' *'}
                disabled={!isEditEnabled}
              />
              <StyledInput
                control={control}
                name="firstname"
                title={t('pages.profile.firstName') + ' *'}
                disabled={!isEditEnabled}
              />
            </div>
          </StyledFormSection>
          <HorizontalLine />
        </>
      )}
      <StyledFormSection sectionTitle={t('pages.profile.mainContact')}>
        <div className="flex flex-col md:flex-row gap-4">
          <StyledInput
            control={control}
            title={t('pages.profile.lastName')}
            name="lastname"
            disabled={!isEditEnabled}
          />
          <StyledInput
            control={control}
            title={t('pages.profile.firstName')}
            name="firstname"
            disabled={!isEditEnabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-4">
          <StyledInput
            control={control}
            title={t('pages.profile.email')}
            name="email"
            type="email"
            disabled={!isEditEnabled || !isAdmin}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="w-1/2">
            <StyledInput
              control={control}
              title={t('pages.profile.password')}
              name="password"
              type="password"
              disabled={!isEditEnabled}
            />
          </div>
        </div>
      </StyledFormSection>
      {isAdmin && (
        <>
          <HorizontalLine />
          <StyledFormSection sectionTitle={t('pages.profile.adminInfo')}>
            <StyledInput
              control={control}
              name="company.address"
              title={t('pages.profile.address')}
              disabled={!isEditEnabled}
              placeholder={t('pages.profile.addAddress')}
            />
            <div className="flex flex-col md:flex-row gap-4">
              <StyledInput
                control={control}
                name="company.city"
                title={t('pages.profile.city')}
                disabled={!isEditEnabled}
                placeholder={t('pages.profile.addCity')}
              />
              <StyledInput
                control={control}
                name="company.postalCode"
                type="number"
                title={t('pages.profile.postalCode')}
                hideNumberFieldArrows
                disabled={!isEditEnabled}
                placeholder={t('pages.profile.addPostalCode')}
              />
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <StyledSelect
                options={COUNTRIES}
                control={control}
                name="company.country"
                title={t('pages.profile.country') + ' *'}
                disabled={!isEditEnabled}
                placeholder={t('pages.profile.selectCountry')}
                onChange={changeCountryWithCurrency}
              />
              <StyledSelect
                options={CURRENCIES}
                control={control}
                name="company.currency"
                title={t('pages.profile.currency') + ' *'}
                disabled={!isEditEnabled}
                placeholder={t('pages.profile.selectCurrency')}
              />
            </div>
            <StyledInput
              control={control}
              name="company.website"
              title={t('pages.profile.website')}
              disabled={!isEditEnabled}
              placeholder={t('pages.profile.websitePlaceholder')}
            />
            <ReceiverContacts control={control} isViewMode={!isEditEnabled} />
            <ReceiverReservationEmails
              control={control}
              isViewMode={!isEditEnabled}
            />
          </StyledFormSection>
        </>
      )}
      {isLoading ? (
        <Loader />
      ) : (
        isSuccess &&
        bills.length > 0 && (
          <>
            <HorizontalLine />
            <StyledFormSection
              sectionTitle={t('pages.profile.bills')}
              shouldTakeFullWidth
            >
              <div className="w-full">
                <StyledTable
                  columns={getTranslatedConst(BILL_COLUMN_DEF)}
                  data={bills}
                  getRowId={(row) => row.id}
                  onDownloadRow={(row) => {
                    window.open(row.original.fileUrl, '_blank');
                  }}
                  sorting={sorting}
                  setSorting={setSorting}
                  clientSideSorting
                />
              </div>
            </StyledFormSection>
          </>
        )
      )}
    </>
  );
};

export default ReceiverProfileGeneralTabForm;
